/**
 * Quota-safe cache for view snapshots (C2_Cache_ keys).
 * Enforces an LRU index with size budgets and never throws on quota errors.
 */

import { STORAGE_KEYS } from './constants';

const CACHE_PREFIX = 'C2_Cache_';
const INDEX_KEY = 'C2_Cache_Index';

/** Index keys from earlier versions; purged on reconcile. */
const LEGACY_INDEX_KEYS = ['C2_Cache_Dashboard_Index'];

const MAX_ENTRY_BYTES = 700 * 1024;
const MAX_TOTAL_BYTES = 2.5 * 1024 * 1024;
const MAX_ENTRIES = 12;
const MAX_WRITE_ATTEMPTS = 6;

interface CacheIndexEntry {
  key: string;
  size: number;
  ts: number;
}

/** Blocks writes after logout so in-flight requests cannot re-seed the cache. */
let writesBlocked = false;

function sessionActive(): boolean
{
  try
  {
    return !!localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
  }
  catch
  {
    return false;
  }
}

/** localStorage stores UTF-16, so two bytes per char. */
function byteSize(key: string, value: string): number
{
  return (key.length + value.length) * 2;
}

function isQuotaError(error: unknown): boolean
{
  if (!(error instanceof DOMException)) return false;
  return (
    error.name === 'QuotaExceededError' ||
    error.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
    error.code === 22 ||
    error.code === 1014
  );
}

function cacheKeysInStorage(): string[]
{
  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i++)
  {
    const key = localStorage.key(i);
    if (key && key.startsWith(CACHE_PREFIX) && key !== INDEX_KEY)
    {
      keys.push(key);
    }
  }
  return keys;
}

/** Reads the index and reconciles it with what is actually in storage. */
function readIndex(): CacheIndexEntry[]
{
  let index: CacheIndexEntry[] = [];
  try
  {
    const raw = localStorage.getItem(INDEX_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (Array.isArray(parsed))
    {
      index = parsed.filter(
        (entry): entry is CacheIndexEntry =>
          !!entry && typeof entry.key === 'string' && entry.key !== INDEX_KEY,
      );
    }
  }
  catch
  {
    index = [];
  }

  LEGACY_INDEX_KEYS.forEach(key => localStorage.removeItem(key));

  const present = new Set(cacheKeysInStorage());
  const tracked = new Set(index.map(entry => entry.key));
  const reconciled = index.filter(entry => present.has(entry.key));

  // Adopt untracked keys (legacy or other tabs) with ts 0 so they evict first
  present.forEach(key =>
  {
    if (tracked.has(key)) return;
    const value = localStorage.getItem(key) ?? '';
    reconciled.push({ key, size: byteSize(key, value), ts: 0 });
  });

  return reconciled.sort((a, b) => a.ts - b.ts);
}

function writeIndex(index: CacheIndexEntry[]): void
{
  try
  {
    localStorage.setItem(INDEX_KEY, JSON.stringify(index));
  }
  catch
  {
    // If even the index does not fit, nothing is cacheable
    clearViewCache();
  }
}

/** Evicts least-recently-written entries until the incoming size fits. */
function makeRoom(index: CacheIndexEntry[], incomingBytes: number): CacheIndexEntry[]
{
  const survivors = [...index].sort((a, b) => a.ts - b.ts);
  let total = survivors.reduce((sum, entry) => sum + entry.size, 0) + incomingBytes;

  while (survivors.length > 0 && (total > MAX_TOTAL_BYTES || survivors.length >= MAX_ENTRIES))
  {
    const victim = survivors.shift();
    if (!victim) break;
    localStorage.removeItem(victim.key);
    total -= victim.size;
  }

  return survivors;
}

/** Stores a snapshot. Returns false when it was not cached; never throws. */
export function writeViewCache(key: string, value: unknown): boolean
{
  if (typeof window === 'undefined') return false;

  if (writesBlocked)
  {
    if (!sessionActive()) return false;
    writesBlocked = false;
  }

  let serialized: string;
  try
  {
    serialized = JSON.stringify(value);
  }
  catch
  {
    return false;
  }
  if (serialized === undefined) return false;

  const size = byteSize(key, serialized);

  if (size > MAX_ENTRY_BYTES)
  {
    // Drop any stale copy so the next load does not serve it
    removeViewCache(key);
    return false;
  }

  const index = makeRoom(readIndex().filter(entry => entry.key !== key), size);

  for (let attempt = 0; attempt < MAX_WRITE_ATTEMPTS; attempt++)
  {
    try
    {
      localStorage.setItem(key, serialized);
      index.push({ key, size, ts: Date.now() });
      writeIndex(index);
      return true;
    }
    catch (error)
    {
      if (!isQuotaError(error)) return false;

      // Quota is shared with non-cache data, so one eviction may not be enough
      const victim = index.shift();
      if (!victim) break;
      localStorage.removeItem(victim.key);
    }
  }

  writeIndex(index);
  return false;
}

/** Reads a snapshot. Returns null when missing, unparseable or expired. */
export function readViewCache<T>(key: string, maxAgeMs?: number): T | null
{
  if (typeof window === 'undefined') return null;

  let raw: string | null = null;
  try
  {
    raw = localStorage.getItem(key);
  }
  catch
  {
    return null;
  }
  if (!raw) return null;

  if (maxAgeMs !== undefined)
  {
    const entry = readIndex().find(item => item.key === key);
    if (entry && entry.ts > 0 && Date.now() - entry.ts > maxAgeMs)
    {
      removeViewCache(key);
      return null;
    }
  }

  try
  {
    return JSON.parse(raw) as T;
  }
  catch
  {
    removeViewCache(key);
    return null;
  }
}

/**
 * Rewrites every entry whose key starts with `prefix`, keeping its age.
 * Returning null from `patch` drops the entry. Never throws.
 */
export function patchViewCacheByPrefix<T>(prefix: string, patch: (value: T) => T | null): void
{
  if (typeof window === 'undefined') return;

  try
  {
    const index = readIndex();
    let changed = false;

    index
      .filter(entry => entry.key.startsWith(prefix))
      .forEach(entry =>
      {
        const raw = localStorage.getItem(entry.key);
        let next: T | null = null;
        try
        {
          next = raw ? patch(JSON.parse(raw) as T) : null;
        }
        catch
        {
          next = null;
        }

        if (next === null)
        {
          localStorage.removeItem(entry.key);
          entry.size = -1;
          changed = true;
          return;
        }

        const serialized = JSON.stringify(next);
        if (serialized === raw) return;
        try
        {
          localStorage.setItem(entry.key, serialized);
          entry.size = byteSize(entry.key, serialized);
        }
        catch
        {
          // A stale copy is worse than none
          localStorage.removeItem(entry.key);
          entry.size = -1;
        }
        changed = true;
      });

    if (changed) writeIndex(index.filter(entry => entry.size >= 0));
  }
  catch
  {
    // Cache is best-effort
  }
}

/** Removes a single entry and its index record. */
export function removeViewCache(key: string): void
{
  if (typeof window === 'undefined') return;
  localStorage.removeItem(key);
  writeIndex(readIndex().filter(entry => entry.key !== key));
}

/** Removes every cache entry and the index. */
export function clearViewCache(): void
{
  if (typeof window === 'undefined') return;
  cacheKeysInStorage().forEach(key => localStorage.removeItem(key));
  LEGACY_INDEX_KEYS.forEach(key => localStorage.removeItem(key));
  localStorage.removeItem(INDEX_KEY);
}

/** Purges the cache and blocks writes until a new session token exists. */
export function clearViewCacheForLogout(): void
{
  writesBlocked = true;
  clearViewCache();
}
